import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { RefreshCw, Trash2, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface MockAppointment {
  id: number;
  id_paciente: number;
  paciente_nome?: string;
  id_tipo_agenda: number;
  tipo_agenda_descricao?: string;
  id_medico: number;
  medico_nome?: string;
  data_agendamento: string;
  hora_inicio: string;
  hora_fim: string;
  sala_consultorio?: string;
  motivo?: string;
  observacoes?: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA';
  status_agendamento: 'AGENDADO' | 'CONFIRMADO' | 'CANCELADO' | 'AGUARDANDO_CONFIRMACAO' | 'CONCLUIDO';
  notificacao_sms: boolean;
  notificacao_whatsapp: boolean;
  notificacao_email: boolean;
}

interface Medico {
  id: number;
  nome?: string;
  pessoa?: { nome: string };
}

interface TipoAgenda {
  id: number;
  descricao: string;
}

interface TipoAtendimento {
  id: number;
  nome: string;
  cor?: string;
}

interface NovoAgendamentoProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  appointment?: MockAppointment | null;
  selectedPatient?: { id: number; name: string; cpf: string } | null;
  onChangePatient?: () => void;
  medicos: Medico[];
  tiposAgenda: TipoAgenda[];
  tiposAtendimento: TipoAtendimento[];
  availableTimeSlots: string[];
  appointments?: MockAppointment[];
  onSave: (appointment: Omit<MockAppointment, 'id'> | MockAppointment) => void;
  onBuscarHorarios?: (idsMedicos: number[], data: string) => Promise<void>;
  onDelete?: (id: number) => void;
}

export const NovoAgendamento = ({
  isOpen,
  onOpenChange,
  appointment,
  selectedPatient,
  onChangePatient,
  medicos,
  tiposAgenda,
  tiposAtendimento,
  availableTimeSlots,
  appointments = [],
  onSave,
  onBuscarHorarios,
  onDelete,
}: NovoAgendamentoProps) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    id_paciente: "",
    id_tipo_atendimento: "",
    id_medico: "",
    data_agendamento: format(new Date(), "yyyy-MM-dd"),
    hora: "",
    motivo: "",
    observacoes: "",
    status_agendamento: "AGENDADO" as MockAppointment['status_agendamento'],
    // prioridade: "MEDIA" as MockAppointment['prioridade'],
  });

  // Preenche o formulário quando um agendamento é passado ou quando o paciente é selecionado
  useEffect(() => {
    if (appointment) {
      // Garante que os valores sejam strings para os Selects
      const idTipoAtendimento = (appointment.id_tipo_agenda || (appointment as any).id_tipo_atendimento)?.toString() || "";
      const idMedico = appointment.id_medico?.toString() || "";
      const dataAgendamento = appointment.data_agendamento.split('T')[0]; // Garante formato YYYY-MM-DD

      // Normaliza o horário removendo segundos se forem :00
      const normalizarHorario = (horario: string): string => {
        if (!horario) return horario;
        if (horario.match(/^\d{2}:\d{2}:00$/)) {
          return horario.substring(0, 5);
        }
        return horario;
      };

      setFormData({
        id_paciente: appointment.id_paciente.toString(),
        id_tipo_atendimento: idTipoAtendimento,
        id_medico: idMedico,
        data_agendamento: dataAgendamento,
        hora: normalizarHorario(appointment.hora_inicio),
        motivo: appointment.motivo || "",
        observacoes: appointment.observacoes || "",
        status_agendamento: appointment.status_agendamento || "AGENDADO" as MockAppointment['status_agendamento'],
        // prioridade: appointment.prioridade,
      });

      // Busca horários disponíveis quando abre para edição
      if (idMedico && dataAgendamento && onBuscarHorarios) {
        const idsMedicos = [Number(idMedico)];
        onBuscarHorarios(idsMedicos, dataAgendamento);
      }
    } else if (selectedPatient) {
      // Novo agendamento com paciente selecionado
      setFormData({
        id_paciente: selectedPatient.id.toString(),
        id_tipo_atendimento: "",
        id_medico: "",
        data_agendamento: format(new Date(), "yyyy-MM-dd"),
        hora: "",
        motivo: "",
        observacoes: "",
        status_agendamento: "AGENDADO" as MockAppointment['status_agendamento'],
        // prioridade: "MEDIA",
      });
    } else {
      // Reset form quando não há agendamento nem paciente
      setFormData({
        id_paciente: "",
        id_tipo_atendimento: "",
        id_medico: "",
        data_agendamento: format(new Date(), "yyyy-MM-dd"),
        hora: "",
        motivo: "",
        observacoes: "",
        status_agendamento: "AGENDADO" as MockAppointment['status_agendamento'],
        // prioridade: "MEDIA",
      });
    }
  }, [appointment, selectedPatient, isOpen]);

  // Busca horários disponíveis quando médico ou data mudam (mas evita loop durante inicialização)
  const prevMedicoRef = useRef<string>("");
  const prevDataRef = useRef<string>("");

  useEffect(() => {
    // Só busca se médico ou data realmente mudaram (não na inicialização)
    const medicoMudou = prevMedicoRef.current !== formData.id_medico;
    const dataMudou = prevDataRef.current !== formData.data_agendamento;

    if ((medicoMudou || dataMudou) && formData.id_medico && formData.data_agendamento && onBuscarHorarios) {
      const idsMedicos = [Number(formData.id_medico)];
      onBuscarHorarios(idsMedicos, formData.data_agendamento);
    }

    // Atualiza as refs
    prevMedicoRef.current = formData.id_medico;
    prevDataRef.current = formData.data_agendamento;
  }, [formData.id_medico, formData.data_agendamento, onBuscarHorarios]);

  // Calcula horários disponíveis baseado no médico e data selecionados
  const getAvailableTimeSlots = () => {
    if (!formData.id_medico || !formData.data_agendamento) {
      return [];
    }

    const idMedico = Number(formData.id_medico);
    const ocupados = appointments
      .filter(
        (apt) =>
          apt.id_medico === idMedico &&
          apt.data_agendamento === formData.data_agendamento &&
          apt.status_agendamento !== "CANCELADO" &&
          apt.id !== appointment?.id // Exclui o próprio agendamento se estiver editando
      )
      .map((apt) => apt.hora_inicio);

    const horariosDisponiveis = availableTimeSlots.filter((time) => !ocupados.includes(time));

    // Se está editando e o horário atual não está na lista, adiciona ele
    if (appointment && appointment.hora_inicio && !horariosDisponiveis.includes(appointment.hora_inicio)) {
      horariosDisponiveis.push(appointment.hora_inicio);
      horariosDisponiveis.sort();
    }

    // Se o formulário já tem um horário selecionado e não está na lista, adiciona ele também
    if (formData.hora && !horariosDisponiveis.includes(formData.hora)) {
      horariosDisponiveis.push(formData.hora);
      horariosDisponiveis.sort();
    }

    return horariosDisponiveis;
  };

  const checkConflict = (data_agendamento: string, hora: string, id_medico: number, excludeId?: number) => {
    return appointments.some(
      (apt) =>
        apt.id !== excludeId &&
        apt.data_agendamento === data_agendamento &&
        apt.hora_inicio === hora &&
        apt.status_agendamento !== "CANCELADO" &&
        apt.id_medico === id_medico
    );
  };

  // Calcula o horário de fim baseado no horário de início (assumindo 30 minutos de duração padrão)
  const calculateEndTime = (startTime: string): string => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + 30; // 30 minutos padrão
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.id_paciente || !formData.id_medico || !formData.data_agendamento || !formData.hora || !formData.id_tipo_atendimento) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos obrigatórios",
        variant: "destructive",
      });
      return;
    }

    if (!selectedPatient) {
      toast({
        title: "Erro",
        description: "Paciente não selecionado",
        variant: "destructive",
      });
      return;
    }

    const hora_fim = calculateEndTime(formData.hora);

    if (checkConflict(formData.data_agendamento, formData.hora, Number(formData.id_medico), appointment?.id)) {
      toast({
        title: "Conflito de horário",
        description: "Já existe um agendamento neste horário",
        variant: "destructive",
      });
      return;
    }

    const appointmentData = {
      ...(appointment ? { id: appointment.id } : {}),
      id_paciente: Number(formData.id_paciente),
      paciente_nome: selectedPatient.name,
      id_tipo_atendimento: Number(formData.id_tipo_atendimento),
      id_medico: Number(formData.id_medico),
      data_agendamento: formData.data_agendamento,
      hora_inicio: formData.hora,
      hora_fim: hora_fim,
      motivo: formData.motivo || undefined,
      observacoes: formData.observacoes || undefined,
      // prioridade: formData.prioridade,
      prioridade: "MEDIA" as MockAppointment['prioridade'], // Valor padrão temporário
      status_agendamento: formData.status_agendamento,
      duracao_minutos: 30, // Valor padrão
      notificacao_sms: false,
      notificacao_whatsapp: false,
      notificacao_email: false,
    };

    onSave(appointmentData);
    onOpenChange(false);

    toast({
      title: appointment ? "Agendamento atualizado" : "Agendamento criado",
      description: `Agendamento ${appointment ? "atualizado" : "criado"} com sucesso!`,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{appointment ? "Editar Agendamento" : "Novo Agendamento"}</DialogTitle>
          <DialogDescription>
            {appointment ? "Edite as informações do agendamento" : "Preencha os dados para criar um novo agendamento"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Card do Paciente */}
          {selectedPatient ? (
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{selectedPatient.name}</p>
                    {selectedPatient.cpf && (
                      <p className="text-sm text-muted-foreground">
                        CPF: {selectedPatient.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")}
                      </p>
                    )}
                  </div>
                </div>
                {onChangePatient && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onChangePatient}
                    className="gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Trocar Paciente
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            <Card className="p-4 border-dashed">
              <div className="text-center text-muted-foreground">
                <p>Nenhum paciente selecionado</p>
                {onChangePatient && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onChangePatient}
                    className="mt-2"
                  >
                    Selecionar Paciente
                  </Button>
                )}
              </div>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-4">

            <div className="space-y-2">
              <Label htmlFor="id_tipo_atendimento">Tipo de Atendimento *</Label>
              <Select value={formData.id_tipo_atendimento} onValueChange={(value) => setFormData({ ...formData, id_tipo_atendimento: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposAtendimento.length > 0 ? (
                    tiposAtendimento.map((tipo) => (
                      <SelectItem key={tipo.id} value={tipo.id.toString()}>
                        {tipo.nome}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Nenhum tipo de atendimento disponível
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="id_medico">Médico Responsável *</Label>
              <Select value={formData.id_medico} onValueChange={(value) => {
                setFormData({ ...formData, id_medico: value, hora: "" }); // Limpa o horário ao mudar o médico
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o médico" />
                </SelectTrigger>
                <SelectContent>
                  {medicos.map((medico) => {
                    const nomeMedico = medico.pessoa?.nome || medico.nome || '';
                    return (
                      <SelectItem key={medico.id} value={medico.id.toString()}>
                        {nomeMedico}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="data_agendamento">Data *</Label>
              <Input
                id="data_agendamento"
                type="date"
                value={formData.data_agendamento}
                onChange={(e) => {
                  setFormData({ ...formData, data_agendamento: e.target.value, hora: "" }); // Limpa o horário ao mudar a data
                }}
              />
            </div>

            {formData.id_medico && formData.data_agendamento && (
              <div className="space-y-2">
                <Label htmlFor="hora">Horário *</Label>
                <Select value={formData.hora} onValueChange={(value) => setFormData({ ...formData, hora: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o horário" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableTimeSlots().length > 0 ? (
                      getAvailableTimeSlots().map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        Nenhum horário disponível
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* <div className="space-y-2">
              <Label htmlFor="prioridade">Prioridade</Label>
              <Select value={formData.prioridade} onValueChange={(value) => setFormData({ ...formData, prioridade: value as MockAppointment['prioridade'] })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BAIXA">Baixa</SelectItem>
                  <SelectItem value="MEDIA">Média</SelectItem>
                  <SelectItem value="ALTA">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div> */}

            {appointment && (
              <div className="space-y-2">
                <Label htmlFor="status_agendamento">Status do Agendamento *</Label>
                <Select
                  value={formData.status_agendamento || "AGENDADO"}
                  onValueChange={(value) => setFormData({ ...formData, status_agendamento: value as MockAppointment['status_agendamento'] })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AGENDADO">Agendado</SelectItem>
                    <SelectItem value="CONFIRMADO">Confirmado</SelectItem>
                    <SelectItem value="AGUARDANDO_CONFIRMACAO">Aguardando Confirmação</SelectItem>
                    <SelectItem value="CANCELADO">Cancelado</SelectItem>
                    <SelectItem value="CONCLUIDO">Concluído</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="motivo">Motivo</Label>
            <Textarea
              id="motivo"
              value={formData.motivo}
              onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
              placeholder="Descreva o motivo da consulta..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              placeholder="Observações adicionais..."
              rows={2}
            />
          </div>

          <div className="flex justify-between items-center w-full mt-4">
            {appointment && onDelete ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive" className="gap-2">
                    <Trash2 className="h-4 w-4" />
                    Excluir
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação não pode ser desfeita. O agendamento {appointment.paciente_nome ? `de ${appointment.paciente_nome}` : ''} será apagado permanentemente.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        onDelete(appointment.id);
                        onOpenChange(false);
                      }}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Excluir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <div />
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit">{appointment ? "Atualizar" : "Agendar"}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

